/**
 * Fetches savings plans and landing layout from the public API.
 */

import { useEffect, useState } from 'react'
import { mergeLandingLayout } from '../../lib/landingLayout'
import type { LandingSectionId } from '../../types/firestore'
import type { PublicSavingsPlan } from '../../lib/savingsPlans'
import type { Paise } from '../../lib/money'

export interface PublicFundImpact {
  totalDisbursedPaise: Paise
  loanCount: number
}

const EMPTY_FUND_IMPACT: PublicFundImpact = {
  totalDisbursedPaise: 0,
  loanCount: 0,
}

interface LandingContentResponse {
  plans: PublicSavingsPlan[]
  landingLayout?: {
    sectionOrder?: LandingSectionId[]
    savingsPlanOrder?: Array<'umra' | 'general'>
  }
  fundImpact?: PublicFundImpact
}

export interface UseSavingsPlansResult {
  plans: PublicSavingsPlan[]
  sectionOrder: LandingSectionId[]
  fundImpact: PublicFundImpact
  loading: boolean
  error: string | null
}

export function useSavingsPlans(): UseSavingsPlansResult {
  const [plans, setPlans] = useState<PublicSavingsPlan[]>([])
  const [sectionOrder, setSectionOrder] = useState(mergeLandingLayout(undefined).sectionOrder)
  const [fundImpact, setFundImpact] = useState<PublicFundImpact>(EMPTY_FUND_IMPACT)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const res = await fetch('/api/savings-plans')
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const body = (await res.json()) as LandingContentResponse
        if (!cancelled) {
          setPlans(body.plans ?? [])
          setSectionOrder(mergeLandingLayout(body.landingLayout).sectionOrder)
          setFundImpact(body.fundImpact ?? EMPTY_FUND_IMPACT)
          setError(null)
        }
      } catch (err) {
        console.error('[savings-plans] fetch failed', err)
        if (!cancelled) {
          setPlans([])
          setSectionOrder(mergeLandingLayout(undefined).sectionOrder)
          setFundImpact(EMPTY_FUND_IMPACT)
          setError('Could not load savings plans.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [])

  return { plans, sectionOrder, fundImpact, loading, error }
}

/** Sample figures for the admin landing preview. */
export const PREVIEW_FUND_IMPACT: PublicFundImpact = {
  totalDisbursedPaise: 80_00_000,
  loanCount: 24,
}
